import { Card, CardBody } from "@heroui/react";
import { ShieldAlert, Lock } from "lucide-react";
import type { ReactNode } from "react";

interface PermissionPlaceholderProps {
    /** Title of the section that requires permission */
    title?: string;
    /** Description of what permission is needed */
    description?: string;
    /** Size variant */
    size?: "sm" | "md" | "lg";
    /** Custom icon */
    icon?: ReactNode;
}

/**
 * PermissionPlaceholder
 * Displays a message when a user doesn't have permission to view a component
 * Used for components that cannot be completely hidden but need to show restricted access
 * 
 * @example
 * <ProtectedComponent 
 *   permission="stats:read" 
 *   fallback={<PermissionPlaceholder title="Statistics" />}
 * >
 *   <StatsComponent />
 * </ProtectedComponent>
 */
export const PermissionPlaceholder = ({
    title = "This Section",
    description = "You need additional permissions to view this content. Please contact your administrator.",
    size = "md",
    icon,
}: PermissionPlaceholderProps) => {
    const sizeClasses = {
        sm: "p-4",
        md: "p-6",
        lg: "p-8",
    };

    const iconSizes = {
        sm: "h-8 w-8",
        md: "h-12 w-12",
        lg: "h-16 w-16",
    };

    const textSizes = {
        sm: {
            title: "text-sm",
            description: "text-xs",
        },
        md: {
            title: "text-base",
            description: "text-sm",
        },
        lg: {
            title: "text-lg",
            description: "text-base",
        },
    };

    return (
        <Card className="border border-dashed border-warning/50 bg-warning/5 rounded shadow-none">
            <CardBody className={sizeClasses[size]}>
                <div className="flex flex-col items-center justify-center text-center space-y-3">
                    <div className={`flex items-center justify-center rounded-full bg-warning/10 ${iconSizes[size] === "h-8 w-8" ? "p-2" : iconSizes[size] === "h-12 w-12" ? "p-3" : "p-4"}`}>
                        {icon || <Lock className={`${iconSizes[size]} text-warning`} />}
                    </div>
                    <div className="space-y-1">
                        <h3 className={`font-semibold text-foreground flex items-center justify-center gap-2 ${textSizes[size].title}`}>
                            <ShieldAlert className="h-4 w-4 text-warning" />
                            {title} - Permission Required
                        </h3>
                        <p className={`text-muted-foreground ${textSizes[size].description}`}>
                            {description}
                        </p>
                    </div>
                </div>
            </CardBody>
        </Card>
    );
};

/**
 * InlinePermissionPlaceholder
 * A more compact version for inline use in grids or lists
 */
export const InlinePermissionPlaceholder = ({
    message = "Permission required to view this content",
}: {
    message?: string;
}) => {
    return (
        <div className="flex items-center justify-center p-4 rounded border border-dashed border-warning/50 bg-warning/5">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Lock className="h-4 w-4 text-warning" />
                <span>{message}</span>
            </div>
        </div>
    );
};
